import axios from 'axios'
import { AnimalDtoSpecies, CreateAnimalBodyDtoSex, LoginResponseDto } from '../gen/backendClient'
import { getBackendBaseUrl } from './client'

// Written by hand on purpose: the generated client is not regenerated for these endpoints.

export interface GuestAdoptionCheckoutAdopterDto {
  id: string
  civility?: string | null
  firstName: string
  lastName: string
  email: string
  phoneNumber?: string | null
  address?: string | null
  postalCode?: string | null
  city?: string | null
  country?: string | null
  hasPassword: boolean
}

export interface GuestAdoptionCheckoutAnimalDto {
  id: string
  name: string
  species?: AnimalDtoSpecies | null
  breed?: string | null
  sex?: CreateAnimalBodyDtoSex | null
  profileImageKey?: string | null
}

export interface GuestAdoptionCheckoutOrganizationDto {
  id: string
  name: string
  profileImageKey?: string | null
}

export interface GuestAdoptionCheckoutDto {
  token: string
  status: 'awaiting-payment' | 'paid'
  amountInCents: number
  donationInCents?: number | null
  expiresAt: string
  paidAt?: string | null
  attemptId: string
  adopter: GuestAdoptionCheckoutAdopterDto
  animal: GuestAdoptionCheckoutAnimalDto
  organization: GuestAdoptionCheckoutOrganizationDto
}

export interface GuestAdoptionContactBodyDto {
  civility?: string
  firstName?: string
  lastName?: string
  phoneNumber?: string
  address?: string
  postalCode?: string
  city?: string
  country?: string
}

export interface GuestAdoptionPaymentIntentDto {
  clientSecret: string
  amountInCents: number
  donationInCents: number
  totalInCents: number
}

// These endpoints are public: the checkout token of the link is the only credential. They go through their own
// axios instance so that the interceptor of `backendClient` never attaches the JWT of a connected adopter.
const guestAdoptionsBackendClient = axios.create({
  baseURL: `${getBackendBaseUrl()}/v1`,
})

const getCheckoutUrl = (token: string): string => `/adopters-platform/guest-adoptions/${encodeURIComponent(token)}`

export class GuestAdoptionsClient {
  public static async getCheckout(token: string): Promise<GuestAdoptionCheckoutDto> {
    const response = await guestAdoptionsBackendClient.get<GuestAdoptionCheckoutDto>(getCheckoutUrl(token))

    return response.data
  }

  public static async updateContact(
    token: string,
    contact: GuestAdoptionContactBodyDto
  ): Promise<GuestAdoptionCheckoutDto> {
    const response = await guestAdoptionsBackendClient.put<GuestAdoptionCheckoutDto>(
      `${getCheckoutUrl(token)}/contact`,
      contact
    )

    return response.data
  }

  public static async createPaymentIntent(
    token: string,
    donationInCents: number
  ): Promise<GuestAdoptionPaymentIntentDto> {
    const response = await guestAdoptionsBackendClient.post<GuestAdoptionPaymentIntentDto>(
      `${getCheckoutUrl(token)}/payment-intent`,
      { donationInCents }
    )

    return response.data
  }

  public static async createSession(token: string): Promise<string> {
    const response = await guestAdoptionsBackendClient.post<LoginResponseDto>(`${getCheckoutUrl(token)}/session`)

    const accessToken = response.data.access_token
    if (!accessToken) {
      throw new Error('No token in the response')
    }

    return accessToken
  }

  // The request URL contains the checkout token: views only read the status, they never log or forward the error.
  public static getErrorStatus(error: unknown): number | undefined {
    return axios.isAxiosError(error) ? error.response?.status : undefined
  }
}
